import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navigation, contact } from '../../data/content';
import { useEmail } from '../../context/EmailContext';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { handleQuoteRequest, loadingId } = useEmail();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-soft'
          : 'bg-white'
      }`}
    >
      {/* Retirement notice */}
      <div className="bg-amber-700 text-amber-50">
        <div className="container-wide py-2.5 text-center text-sm font-semibold tracking-wide">
          Notice: Aniko is retiring and GAAS Analytical is no longer accepting new customers. Thank you for your support over the years.
        </div>
      </div>

      {/* Top bar */}
      <div className="bg-gradient-to-r from-primary-dark via-primary to-primary-dark text-white">
        <div className="container-wide py-2.5 flex justify-end items-center gap-6 text-sm">
          <a
            href={contact.phoneHref}
            className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            <span className="font-medium">{contact.phone}</span>
          </a>
          <a
            href={contact.emailHref}
            className="hidden sm:flex items-center gap-2 text-white/90 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            <span className="font-medium">{contact.email}</span>
          </a>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container-wide py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex-shrink-0 group">
            <img
              src="/images/logo.png"
              alt="GAAS Analytical"
              className="h-14 w-auto transition-transform duration-200 group-hover:scale-105"
            />
          </Link>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  location.pathname === item.href
                    ? 'text-primary bg-primary-50'
                    : 'text-text-light hover:text-primary hover:bg-gray-50'
                }`}
              >
                {item.text}
                {location.pathname === item.href && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
                )}
              </Link>
            ))}
            <button
              onClick={() => handleQuoteRequest(undefined, 'header')}
              disabled={loadingId === 'header'}
              className="ml-4 btn-primary text-sm py-2.5 relative"
            >
              <span className={loadingId === 'header' ? 'invisible' : ''}>Get a Quote</span>
              {loadingId === 'header' && (
                <svg className="w-5 h-5 animate-spin absolute inset-0 m-auto" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden p-2 text-text-light hover:text-primary hover:bg-gray-50 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-100 animate-fade-in">
            <div className="flex flex-col gap-1 pt-4">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname === item.href
                      ? 'text-primary bg-primary-50'
                      : 'text-text-light hover:text-primary hover:bg-gray-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.text}
                </Link>
              ))}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleQuoteRequest(undefined, 'header-mobile');
                }}
                disabled={loadingId === 'header-mobile'}
                className="mt-2 btn-primary text-sm justify-center relative"
              >
                <span className={loadingId === 'header-mobile' ? 'invisible' : ''}>Get a Quote</span>
                {loadingId === 'header-mobile' && (
                  <svg className="w-5 h-5 animate-spin absolute inset-0 m-auto" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
